import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Badge } from "@chakra-ui/react";
import { Eye, Archive, Plus, EllipsisVertical, ShieldAlert, Edit } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import AddCompanySettingsDrawer from "./components/AddCompanySettingsDrawer";
import ViewCompanySettingsDrawer from "./components/ViewCompanySettingsDrawer";
import EditCompanySettingsDrawer from "./components/EditCompanySettingsDrawer";
import DeleteCompanySettingsModal from "./components/DeleteCompanySettingsModal";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchCompanySettingsAction,
  createCompanySettingsAction,
  updateCompanySettingsAction,
  deleteCompanySettingsAction,
} from "../../../state/actions/companySettingsActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

const ManageCompanySettingsTab = () => {
  const dispatch = useDispatch();
  const notify = useCustomNotification();

  const { companySettings, loading } = useSelector(
    (state) => state.companySettings
  );

  const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setEditDrawerOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    dispatch(fetchCompanySettingsAction());
  }, [dispatch]);

  const openAddDrawer = () => setAddDrawerOpen(true);
  const closeAddDrawer = () => setAddDrawerOpen(false);

  const openViewDrawer = (company) => {
    setSelectedCompany(company);
    setViewDrawerOpen(true);
  };

  const openEditDrawer = (company) => {
    setSelectedCompany(company);
    setEditDrawerOpen(true);
  };

  const openDeleteModal = (company) => {
    setSelectedCompany(company);
    setDeleteModalOpen(true);
  };

  const handleAddCompanySettings = async (data) => {
    const response = await dispatch(createCompanySettingsAction(data));

    if (response.success) {
      closeAddDrawer();
      notify({
        type: "success",
        message: "Created",
        description: "Company settings created successfully.",
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to create company settings.",
      });
    }
  };

  const handleUpdateCompanySettings = async (data) => {
    if (!selectedCompany) return;

    const response = await dispatch(
      updateCompanySettingsAction(selectedCompany.id, data)
    );

    if (response.success) {
      setEditDrawerOpen(false);
      notify({
        type: "success",
        message: "Updated",
        description: "Company settings updated successfully.",
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to update company settings.",
      });
    }
  };

  const handleConfirmDelete = async () => {
    const response = await dispatch(
      deleteCompanySettingsAction(selectedCompany.id)
    );

    if (response.success) {
      setDeleteModalOpen(false);
      notify({
        type: "success",
        message: "Deleted",
        description: "Company settings deleted successfully.",
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to delete company settings.",
      });
    }
  };

  const generateMenuItems = (row) => [
    {
      icon: <Eye size={16} />,
      text: "View Details",
      onClick: () => openViewDrawer(row.original),
    },
    {
      icon: <Edit size={16} />,
      text: "Edit",
      onClick: () => openEditDrawer(row.original),
    },
    {
      icon: <Archive size={16} />,
      text: "Delete",
      onClick: () => openDeleteModal(row.original),
      extraIcon: <ShieldAlert size={16} color="pink" />,
    },
  ];

  const columnsData = [
    { Header: "Company Name", accessor: "company_name" },
    { Header: "VAT Number", accessor: "vat_number" },
    { Header: "VAT %", accessor: "vat_percent" },
   {
    Header: "Active",
    accessor: "is_active",
    Cell: ({ value }) => (
        <StatusBadge
            label={value ? "Yes" : "No"}
            variant={getStatusVariant(value)}
        />
    ),
},
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Flex justifyContent="end" mb={4} pt={{ base: "120px", md: "50px" }}>
        {loading ? (
          <Skeleton width="180px" height="40px" borderRadius="md" />
        ) : (
          <GradientButton
            icon={<Plus size={16} />}
            height="40px"
            mr={5}
            onClick={openAddDrawer}
          >
            Add Company Settings
          </GradientButton>
        )}
      </Flex>

      <ReusableTable
        columnsData={columnsData}
        tableData={companySettings || []}
        isLoading={loading}
      />

      <AddCompanySettingsDrawer
        isOpen={isAddDrawerOpen}
        onClose={closeAddDrawer}
        onCreate={handleAddCompanySettings}
      />

      <ViewCompanySettingsDrawer
        isOpen={isViewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        company={selectedCompany}
      />

      <EditCompanySettingsDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        company={selectedCompany}
        onUpdate={handleUpdateCompanySettings}
      />

      <DeleteCompanySettingsModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        company={selectedCompany}
      />
    </Box>
  );
};

export default ManageCompanySettingsTab;
